import React, { useCallback, useEffect, useState } from "react";
import { FaCreditCard } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const GATEWAYS = [
  { value: "", label: "Select gateway" },
  { value: "razorpay", label: "Razorpay" },
  { value: "payu", label: "PayU" },
];

const PaymentSetup = () => {
  const { projectId } = useProjectPhone();
  const [configId, setConfigId] = useState("");
  const [gateway, setGateway] = useState("");
  const [steps, setSteps] = useState({
    addMethod: false,
    config: false,
    gateway: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/getwhatsapppayment?projectId=${projectId}`,
      );
      const data = res?.data;
      if (data) {
        setConfigId(data.payment_configuration_id || "");
        setGateway(data.gateway || "");
        setSteps({
          addMethod: true,
          config: Boolean(data.payment_configuration_id),
          gateway: Boolean(data.gateway),
        });
      } else {
        setConfigId("");
        setGateway("");
        setSteps({ addMethod: false, config: false, gateway: false });
      }
    } catch (err) {
      setError(err.message || "Failed to load payment setup");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveConfig = async () => {
    if (!configId.trim()) {
      setError("Enter payment configuration ID from Meta");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await apihubPost(
        "/getInteractedCustomerDetail/savewhatsapppayment",
        { projectId, payment_configuration_id: configId.trim() },
      );
      if (res?.success === false) {
        throw new Error(res.message || "Failed to save configuration");
      }
      setSteps((s) => ({ ...s, addMethod: true, config: true }));
      setMessage("Payment configuration saved");
    } catch (err) {
      setError(err.message || "Failed to save payment configuration");
    } finally {
      setSaving(false);
    }
  };

  const saveGateway = async () => {
    if (!steps.config) {
      setError("Save payment configuration first");
      return;
    }
    if (!gateway) {
      setError("Select a payment gateway");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await apihubPost(
        "/getInteractedCustomerDetail/savewhatsapppayment",
        { projectId, gateway },
      );
      if (res?.success === false) {
        throw new Error(res.message || "Failed to save gateway");
      }
      setSteps((s) => ({ ...s, gateway: true }));
      setMessage("Payment gateway saved");
    } catch (err) {
      setError(err.message || "Failed to save gateway");
    } finally {
      setSaving(false);
    }
  };

  const complete = steps.config && steps.gateway;

  if (loading) {
    return (
      <div className="biz-panel-page">
        <p className="biz-table-message">Loading payment setup…</p>
      </div>
    );
  }

  return (
    <div className="biz-panel-page biz-paymentsetup-page">
      <div className="biz-panel-header">
        <h1>
          <FaCreditCard />
          WhatsApp Pay
        </h1>
      </div>

      {complete && (
        <p className="biz-banner biz-banner-success">
          WhatsApp payment setup completed successfully
        </p>
      )}
      {message && <p className="biz-banner biz-banner-success">{message}</p>}
      {error && <p className="biz-banner biz-banner-error">{error}</p>}

      <div className="biz-paymentsetup-card">
        <h2>How to Setup WhatsApp Pay</h2>
        <p>
          Add a payment method in Meta, save your payment configuration, and
          select a gateway to start collecting payments.
        </p>
      </div>

      <ol className="biz-paymentsetup-steps">
        <li className={steps.addMethod ? "done" : ""}>
          <span className="biz-step-num">01</span>
          <div>
            <h3>Add payment method in Meta</h3>
            <p>Configure WhatsApp Pay in your Meta Business Manager.</p>
          </div>
        </li>

        <li className={steps.config ? "done" : ""}>
          <span className="biz-step-num">02</span>
          <div>
            <h3>Payment configuration ID</h3>
            <input
              type="text"
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              placeholder="Payment configuration ID"
              disabled={saving}
            />
            <button
              type="button"
              className="biz-btn-primary"
              onClick={saveConfig}
              disabled={saving}
            >
              Save configuration
            </button>
          </div>
        </li>

        <li className={steps.gateway ? "done" : ""}>
          <span className="biz-step-num">03</span>
          <div>
            <h3>Payment Gateway</h3>
            <select
              value={gateway}
              onChange={(e) => setGateway(e.target.value)}
              disabled={!steps.config || saving}
            >
              {GATEWAYS.map((g) => (
                <option key={g.value || "empty"} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="biz-btn-primary"
              onClick={saveGateway}
              disabled={!steps.config || saving || !gateway}
            >
              Save gateway
            </button>
          </div>
        </li>

        <li className={complete ? "done" : ""}>
          <span className="biz-step-num">04</span>
          <div>
            <h3>Create Order Message</h3>
            <p>
              You are ready to send WhatsApp Order Payment messages using the
              WhatsApp Business API.
            </p>
          </div>
        </li>
      </ol>
    </div>
  );
};

export default PaymentSetup;
